/* eslint-disable @typescript-eslint/no-namespace */
/* eslint-disable no-prototype-builtins */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  UnknownKeysParam,
  ZodArray,
  ZodNullable,
  ZodObject,
  ZodOptional,
  ZodRawShape,
  ZodTuple,
  ZodTupleItems,
  ZodTypeAny,
} from 'zod'

export namespace Utils {
  /**
   * Pick keys from object, supports nested.key.paths for partial object copying
   *
   * Does not include keys missing from target object
   *
   * @param obj
   * @param paths
   */
  export function pick<T extends object, U extends keyof T>(
    obj: T,
    paths: Array<string>,
  ): Pick<T, U> {
    const ret = Object.create(null)

    const pathsPlusChildren = paths.reduce<{ [key: string]: Array<string> }>(
      (pathsMap, path) => {
        const [rootKey, ...extraKeys] = path.split('.')
        pathsMap[rootKey] = pathsMap[rootKey] ?? []
        if (extraKeys.length > 0) {
          pathsMap[rootKey].push(extraKeys.join('.'))
        }

        return pathsMap
      },
      {},
    )

    for (const [path, children] of Object.entries(pathsPlusChildren)) {
      if (obj.hasOwnProperty(path)) {
        if (children.length === 0) {
          ret[path] = obj[path]
        } else {
          ret[path] = pick(obj[path], children)
        }
      }
    }
    return ret
  }

  export function groupBy(array, key): any {
    return array.reduce((acc, obj) => {
      const property = obj[key]
      acc[property] = acc[property] || []
      acc[property].push(obj)
      return acc
    }, {})
  }

  export const cleanArray = (
    values: any[],
    compareFunc: (value) => any,
  ): any[] => values.filter((value: string) => compareFunc(value))
}

export type Subset<K> = {
  [attr in keyof K]?: K[attr] extends object ? Subset<K[attr]> : K[attr]
}

type ZodObjectMapper<T extends ZodRawShape, U extends UnknownKeysParam> = (
  o: ZodObject<T>,
) => ZodObject<T, U>

function deepApplyObject(
  schema: ZodTypeAny,
  map: ZodObjectMapper<any, any>,
): any {
  if (schema instanceof ZodObject) {
    const newShape: Record<string, ZodTypeAny> = {}
    for (const key in schema.shape) {
      const fieldSchema = schema.shape[key]
      newShape[key] = deepApplyObject(fieldSchema, map)
    }
    const newObject = new ZodObject({
      ...schema._def,
      shape: () => newShape,
    })
    return map(newObject)
  } else if (schema instanceof ZodArray) {
    return ZodArray.create(deepApplyObject(schema.element, map))
  } else if (schema instanceof ZodOptional) {
    return ZodOptional.create(deepApplyObject(schema.unwrap(), map))
  } else if (schema instanceof ZodNullable) {
    return ZodNullable.create(deepApplyObject(schema.unwrap(), map))
  } else if (schema instanceof ZodTuple) {
    return ZodTuple.create(
      schema.items.map((item: any) => deepApplyObject(item, map)),
    )
  } else {
    return schema
  }
}

type DeepUnknownKeys<
  T extends ZodTypeAny,
  UnknownKeys extends UnknownKeysParam,
> =
  T extends ZodObject<infer Shape, infer _, infer Catchall>
    ? ZodObject<
        {
          [k in keyof Shape]: DeepUnknownKeys<Shape[k], UnknownKeys>
        },
        UnknownKeys,
        Catchall
      >
    : T extends ZodArray<infer Type, infer Card>
      ? ZodArray<DeepUnknownKeys<Type, UnknownKeys>, Card>
      : T extends ZodOptional<infer Type>
        ? ZodOptional<DeepUnknownKeys<Type, UnknownKeys>>
        : T extends ZodNullable<infer Type>
          ? ZodNullable<DeepUnknownKeys<Type, UnknownKeys>>
          : T extends ZodTuple<infer Items>
            ? {
                [k in keyof Items]: Items[k] extends ZodTypeAny
                  ? DeepUnknownKeys<Items[k], UnknownKeys>
                  : never
              } extends infer PI
              ? PI extends ZodTupleItems
                ? ZodTuple<PI>
                : never
              : never
            : T

type DeepPassthrough<T extends ZodTypeAny> = DeepUnknownKeys<T, 'passthrough'>
export function deepPassthrough<T extends ZodTypeAny>(
  schema: T,
): DeepPassthrough<T> {
  return deepApplyObject(schema, (s) => s.passthrough()) as DeepPassthrough<T>
}

type DeepStrip<T extends ZodTypeAny> = DeepUnknownKeys<T, 'strip'>
export function deepStrip<T extends ZodTypeAny>(schema: T): DeepStrip<T> {
  return deepApplyObject(schema, (s) => s.strip()) as DeepStrip<T>
}

// Helper function to recursively apply .strict() to nested properties
type DeepStrict<T extends ZodTypeAny> = DeepUnknownKeys<T, 'strict'>
export function deepStrict<T extends ZodTypeAny>(
  schema: T,
  error?: string,
): DeepStrict<T> {
  return deepApplyObject(schema, (s) => s.strict(error)) as DeepStrict<T>
}

// Define a DeepPartial type utility
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends Array<infer U>
    ? Array<DeepPartial<U>>
    : T[P] extends object
      ? DeepPartial<T[P]>
      : T[P]
}

// Updated deepMerge to work with DeepPartial
export function deepMerge<T extends object>(
  target: T,
  source: DeepPartial<T>,
): T {
  const output = { ...target }

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      const sourceKey = key as keyof typeof source
      const targetKey = key as keyof typeof target

      if (source[sourceKey] === undefined) return

      // Handle arrays differently - replace the entire array
      if (Array.isArray(source[sourceKey])) {
        ;(output as any)[key] = source[sourceKey]
        return
      }

      if (isObject(source[sourceKey]) && targetKey in target) {
        // If property exists and is an object in both, recursively merge
        if (isObject(target[targetKey])) {
          ;(output as any)[key] = deepMerge(
            target[targetKey] as object,
            source[sourceKey] as object,
          )
          return
        }
      }
      // Otherwise just replace/add the property
      ;(output as any)[key] = source[sourceKey]
    })
  }

  return output
}

// Helper to check if value is an object
export function isObject(item: any): item is object {
  return item && typeof item === 'object' && !Array.isArray(item)
}
